import BlogService from "services/BlogService";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

export const initialState = {
  blogs: [],
  createModalOpen: false,
  currentPage: 1,
  deleteModalOpen: false,
  editModalOpen: false,
  loading: false,
  message: "",
  searchValue: "",
  selectedBlog: null,
  showMessage: false,
  totalItems: 0,
  viewModalOpen: false,
};

// Async Thunks

export const fetchAdminBlogs = createAsyncThunk(
  "blogs/fetchAdmin",
  async (page = 1, { rejectWithValue }) => {
    try {
      return await BlogService.getAll(page);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error fetching blogs",
      );
    }
  },
);

export const createArticle = createAsyncThunk(
  "blogs/create",
  async (data, { rejectWithValue }) => {
    try {
      return await BlogService.create(data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error creating article",
      );
    }
  },
);

export const updateArticle = createAsyncThunk(
  "blogs/update",
  async ({ data, id }, { rejectWithValue }) => {
    try {
      return await BlogService.update(id, data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error updating article",
      );
    }
  },
);

export const deleteArticleById = createAsyncThunk(
  "blogs/delete",
  async (id, { rejectWithValue }) => {
    try {
      await BlogService.deleteById(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error deleting article",
      );
    }
  },
);

export const getBlogById = createAsyncThunk(
  "blogs/getById",
  async (id, { rejectWithValue }) => {
    try {
      return await BlogService.getById(id);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error fetching article details",
      );
    }
  },
);

export const searchBlogs = createAsyncThunk(
  "blogs/search",
  async ({ page, title }, { rejectWithValue }) => {
    try {
      return await BlogService.search(page, title);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error searching blogs",
      );
    }
  },
);

// Blog Slice

export const blogSlice = createSlice({
  extraReducers: (builder) => {
    builder

      .addCase(fetchAdminBlogs.fulfilled, (state, action) => {
        state.loading = false;
        state.blogs = Array.isArray(action.payload) ? action.payload : [];
        state.totalItems = action.payload?.length || 0;
      })
      .addCase(searchBlogs.fulfilled, (state, action) => {
        state.loading = false;
        state.blogs = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(getBlogById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedBlog = action.payload;
      })
      .addCase(createArticle.fulfilled, (state, action) => {
        state.loading = false;
        state.createModalOpen = false;
        if (action.payload?.article) {
          state.blogs.unshift(action.payload.article);
        }
      })
      .addCase(updateArticle.fulfilled, (state, action) => {
        state.loading = false;
        state.editModalOpen = false;
        const updated = action.payload?.article;
        if (updated) {
          const index = state.blogs.findIndex((b) => b.id === updated.id);
          if (index !== -1) {
            state.blogs[index] = updated;
          }
        }
      })
      .addCase(deleteArticleById.fulfilled, (state, action) => {
        state.loading = false;
        state.deleteModalOpen = false;
        state.blogs = state.blogs.filter((blog) => blog.id !== action.payload);
      })

      .addMatcher(
        (action) => action.type.endsWith("/pending"),
        (state) => {
          state.loading = true;
          state.showMessage = false;
        },
      )
      .addMatcher(
        (action) => action.type.endsWith("/rejected"),
        (state, action) => {
          state.loading = false;
          state.message = action.payload;
          state.showMessage = true;
        },
      );
  },
  initialState,
  name: "blog",

  reducers: {
    clearBlogState: (state) => {
      state.blogs = [];
      state.totalItems = 0;
    },
    hideBlogMessage: (state) => {
      state.message = "";
      state.showMessage = false;
    },
    openCreateModal: (state, action) => {
      state.createModalOpen = action.payload;
    },
    openDeleteModal: (state, action) => {
      state.deleteModalOpen = action.payload;
    },
    openEditModal: (state, action) => {
      state.editModalOpen = action.payload;
    },
    openViewModal: (state, action) => {
      state.viewModalOpen = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setSearchValue: (state, action) => {
      state.searchValue = action.payload;
    },
    setSelectedBlog: (state, action) => {
      state.selectedBlog = action.payload;
    },
  },
});

export const {
  clearBlogState,
  hideBlogMessage,
  openCreateModal,
  openDeleteModal,
  openEditModal,
  openViewModal,
  setCurrentPage,
  setSearchValue,
  setSelectedBlog,
} = blogSlice.actions;

export default blogSlice.reducer;
